/**
 * Profile / account-settings hooks.
 *
 * Wraps profileApi/sessionsApi/mfaApi with TanStack Query so each page
 * gets caching, loading/error states, and automatic refetch after
 * mutations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type ChangePasswordRequest,
  type ChangePhoneRequest,
  type DeleteAccountRequest,
  type EmailChangeStartRequest,
  type EmailChangeStartResponse,
  type EmailChangeVerifyResponse,
  type LoginHistoryResponse,
  type MfaBackupCodesRegenerateResponse,
  type MfaBackupCodesStatusResponse,
  type OAuthAccountsResponse,
  type ProfileResponse,
  type SessionsResponse,
  type SetPasswordRequest,
  type UpdatePreferencesRequest,
  type UpdateProfileRequest,
  hasValidAccessToken,
  mfaApi,
  profileApi,
  sessionsApi,
} from '@/lib/api'

export const profileKeys = {
  profile: ['me', 'profile'] as const,
  oauthAccounts: ['me', 'oauth-accounts'] as const,
  backupCodesStatus: ['me', 'backup-codes-status'] as const,
  loginHistory: ['me', 'login-history'] as const,
  sessions: ['auth', 'sessions'] as const,
  mfaStatus: ['mfa', 'status'] as const,
}

// ─── Profile ────────────────────────────────────────────────────────

export function useProfile() {
  return useQuery<ProfileResponse>({
    queryKey: profileKeys.profile,
    queryFn: profileApi.get,
    enabled: hasValidAccessToken(),
    staleTime: 60_000,
  })
}

export function useUpdateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateProfileRequest) => profileApi.update(body),
    onSuccess: (data) => {
      qc.setQueryData(profileKeys.profile, data)
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

export function useUpdatePreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdatePreferencesRequest) => profileApi.updatePreferences(body),
    onSuccess: (data) => {
      qc.setQueryData(profileKeys.profile, data)
    },
  })
}

// ─── Password ───────────────────────────────────────────────────────

export function useChangePassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ChangePasswordRequest) => profileApi.changePassword(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profileKeys.sessions })
      qc.invalidateQueries({ queryKey: profileKeys.profile })
    },
  })
}

export function useSetPassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SetPasswordRequest) => profileApi.setPassword(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profileKeys.profile })
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

// ─── Email change ──────────────────────────────────────────────────

export function useEmailChangeStart() {
  return useMutation<EmailChangeStartResponse, unknown, EmailChangeStartRequest>({
    mutationFn: (body) => profileApi.emailChangeStart(body),
  })
}

export function useEmailChangeVerify() {
  const qc = useQueryClient()
  return useMutation<EmailChangeVerifyResponse, unknown, string>({
    mutationFn: (code) => profileApi.emailChangeVerify(code),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profileKeys.profile })
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

// ─── Phone change ──────────────────────────────────────────────────

export function useChangePhone() {
  const qc = useQueryClient()
  return useMutation<ProfileResponse, unknown, ChangePhoneRequest>({
    mutationFn: (body) => profileApi.changePhone(body),
    onSuccess: (data) => {
      qc.setQueryData(profileKeys.profile, data)
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

// ─── OAuth providers ────────────────────────────────────────────────

export function useOAuthAccounts() {
  return useQuery<OAuthAccountsResponse>({
    queryKey: profileKeys.oauthAccounts,
    queryFn: profileApi.listOAuthAccounts,
    enabled: hasValidAccessToken(),
    staleTime: 60_000,
  })
}

export function useDisconnectOAuth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (provider: 'google' | 'microsoft') => profileApi.disconnectOAuth(provider),
    onSuccess: () => qc.invalidateQueries({ queryKey: profileKeys.oauthAccounts }),
  })
}

// ─── Sessions ───────────────────────────────────────────────────────

export function useSessions() {
  return useQuery<SessionsResponse>({
    queryKey: profileKeys.sessions,
    queryFn: sessionsApi.list,
    enabled: hasValidAccessToken(),
    staleTime: 30_000,
  })
}

export function useRevokeSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => sessionsApi.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: profileKeys.sessions }),
  })
}

export function useRevokeAllOtherSessions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => sessionsApi.revokeAllOthers(),
    onSuccess: () => qc.invalidateQueries({ queryKey: profileKeys.sessions }),
  })
}

// ─── MFA ────────────────────────────────────────────────────────────

export function useMfaStatus() {
  return useQuery({
    queryKey: profileKeys.mfaStatus,
    queryFn: mfaApi.status,
    enabled: hasValidAccessToken(),
    staleTime: 30_000,
  })
}

export function useMfaSetup() {
  return useMutation({ mutationFn: () => mfaApi.setup() })
}

export function useMfaVerify() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => mfaApi.verify(code),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profileKeys.mfaStatus })
      qc.invalidateQueries({ queryKey: profileKeys.profile })
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

export function useMfaDisable() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ password, code }: { password: string; code: string }) =>
      mfaApi.disable(password, code),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profileKeys.mfaStatus })
      qc.invalidateQueries({ queryKey: profileKeys.profile })
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

export function useBackupCodesStatus() {
  return useQuery<MfaBackupCodesStatusResponse>({
    queryKey: profileKeys.backupCodesStatus,
    queryFn: profileApi.backupCodesStatus,
    enabled: hasValidAccessToken(),
    staleTime: 30_000,
  })
}

export function useRegenerateBackupCodes() {
  const qc = useQueryClient()
  return useMutation<MfaBackupCodesRegenerateResponse, unknown, string>({
    mutationFn: (code: string) => profileApi.regenerateBackupCodes(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: profileKeys.backupCodesStatus }),
  })
}

// ─── Account deletion ──────────────────────────────────────────────

export function useDeleteAccount() {
  return useMutation({
    mutationFn: (body: DeleteAccountRequest) => profileApi.deleteAccount(body),
  })
}

// ─── Login history ─────────────────────────────────────────────────

export function useLoginHistory(limit = 20) {
  return useQuery<LoginHistoryResponse>({
    queryKey: [...profileKeys.loginHistory, limit],
    queryFn: () => profileApi.loginHistory(limit),
    enabled: hasValidAccessToken(),
    staleTime: 30_000,
  })
}
